import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';
import { Box, ButtonBase, Stack, Typography } from '@mui/material';
import React from 'react';
import { ImageSelectSpeakExercise } from '../../mockLessonContent';
import { BLUE, cardSx, INTER, NAVY, POPPINS, SLATE, SURFACE_SOFT } from '../../studentStyles';
import AudioPlayButton from '../AudioPlayButton';
import RecordAnswerPanel from './RecordAnswerPanel';
import SpeakingHero from './SpeakingHero';

interface ImageSelectSpeakExerciseCardProps {
    exercise: ImageSelectSpeakExercise;
    onReady: () => void;
}

// "Dialog w/ Image Select & Speak" — every picture is a valid answer, so the
// choice is the student's; the speaking that follows is what gets graded.
export default function ImageSelectSpeakExerciseCard({ exercise, onReady }: ImageSelectSpeakExerciseCardProps) {
    const [pickedIndex, setPickedIndex] = React.useState<number | null>(null);
    const picked = pickedIndex == null ? null : exercise.options[pickedIndex];

    return (
        <Box sx={{ ...cardSx, overflow: 'hidden' }}>
            <SpeakingHero label="CHOOSE & SAY" hanzi={exercise.chineseHanzi} pinyin={exercise.chinesePinyin} />

            <Box sx={{ p: 2.5 }}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box sx={{
                        width: 42, height: 42, borderRadius: '14px', flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: `${BLUE}1A`,
                    }}>
                        <VolumeUpRoundedIcon sx={{ color: BLUE }} />
                    </Box>
                    <Typography sx={{ fontFamily: INTER, fontSize: 13, color: SLATE, flex: 1, minWidth: 0 }}>
                        {exercise.prompt}
                    </Typography>
                    <AudioPlayButton ariaLabel="Play the question" size={40} />
                </Stack>

                <Box sx={{ mt: 2.5, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                    {exercise.options.map((option, index) => {
                        const isPicked = pickedIndex === index;
                        return (
                            <ButtonBase
                                key={option.hanzi}
                                onClick={() => setPickedIndex(index)}
                                sx={{
                                    flexDirection: 'column', p: 1.75, borderRadius: '18px',
                                    backgroundColor: isPicked ? `${BLUE}14` : SURFACE_SOFT,
                                    border: `2px solid ${isPicked ? BLUE : 'transparent'}`,
                                    transition: 'background-color 150ms ease, border-color 150ms ease',
                                }}
                            >
                                <Typography sx={{ fontSize: 38, lineHeight: 1 }}>{option.emoji}</Typography>
                                <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 15, color: NAVY, mt: 1 }}>
                                    {option.hanzi}
                                </Typography>
                                <Typography sx={{ fontFamily: INTER, fontSize: 12, color: SLATE, mt: 0.25 }}>
                                    {option.pinyin}
                                </Typography>
                            </ButtonBase>
                        );
                    })}
                </Box>

                {picked && (
                    <>
                        <Typography sx={{ fontFamily: INTER, fontWeight: 600, fontSize: 11, letterSpacing: '0.08em', color: SLATE, mt: 3, mb: 1 }}>
                            NOW SAY IT
                        </Typography>
                        <Box sx={{ p: 2, borderRadius: '16px', backgroundColor: SURFACE_SOFT, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontFamily: POPPINS, fontWeight: 500, fontSize: 19, color: NAVY, lineHeight: 1.4 }}>
                                    {exercise.answerFrame.hanzi.replace('{}', picked.hanzi)}
                                </Typography>
                                <Typography sx={{ fontFamily: INTER, fontSize: 13, color: SLATE, mt: 0.25 }}>
                                    {exercise.answerFrame.english.replace('{}', picked.english)}
                                </Typography>
                            </Box>
                            <AudioPlayButton ariaLabel="Play the model answer" size={40} />
                        </Box>
                        <RecordAnswerPanel key={picked.hanzi} grades={exercise.grades} onScored={onReady} />
                    </>
                )}
            </Box>
        </Box>
    );
}
